import {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';

const useImageLoadingState = (totalImageCount, onImageError, onLoadingComplete) => {
  const [isLoading, setIsLoading] = useState(true);
  const [totalImages, setTotalImages] = useState(totalImageCount);
  const loadedMainImages = useRef(0);
  const loadedThumbnailImages = useRef(0);

  useEffect(() => {
    setTotalImages(totalImageCount);
    loadedMainImages.current = 0;
    loadedThumbnailImages.current = 0;
    setIsLoading(true);
  }, [totalImageCount]);

  const checkLoadingComplete = useCallback(() => {
    const expectedImagesPerType = totalImages;
    const mainImagesLoaded = loadedMainImages.current >= expectedImagesPerType;

    if (totalImages === 0) {
      setIsLoading(false);
      onLoadingComplete?.();
      return;
    }

    if (mainImagesLoaded) {
      setIsLoading(false);
      onLoadingComplete?.();
    }
  }, [totalImages, onLoadingComplete]);

  const handleImageLoad = useCallback(() => {
    loadedMainImages.current += 1;
    checkLoadingComplete();
  }, [checkLoadingComplete]);

  const handleThumbnailLoad = useCallback(() => {
    loadedThumbnailImages.current += 1;
  }, []);

  const handleImageError = useCallback((imageId) => {
    loadedMainImages.current += 1;
    setTotalImages((prev) => Math.max(0, prev - 1));
    onImageError?.(imageId);
    checkLoadingComplete();
  }, [checkLoadingComplete, onImageError]);

  const resetLoadingState = useCallback(() => {
    setIsLoading(true);
    loadedMainImages.current = 0;
    loadedThumbnailImages.current = 0;
  }, []);

  const getLoadedImagesCount = useCallback(() => loadedMainImages.current, []);

  return {
    isLoading,
    totalImages,
    handleImageLoad,
    handleThumbnailLoad,
    handleImageError,
    resetLoadingState,
    getLoadedImagesCount,
  };
};

export default useImageLoadingState;
